import { type RefObject } from 'react';
import { loadYouTubeAPI, getYT, type YTPlayer } from '@/utils/youtube-api';
import type { SeekFn } from '@/types/required-video';

// 500ms keeps the visual cadence proportional to playback rate — at 2x the
// caption ticks every 0.5s wall-clock instead of 1s, so users actually see
// the speed-up. The first poll always returns 0 credit (sentinel prevTime),
// so a smaller interval also halves the initial lag before the first drop.
const POLL_MS = 500;
const API_FALLBACK_MS = 15_000;

export type State = {
  player: YTPlayer | null;
  pollId: number;
  fbId: number;
};

export type Refs = {
  onReadyRef: { current: (s: SeekFn) => void };
  onTimeRef: { current: (t: number, d: number) => void };
};

type AttachArgs = {
  iframe: HTMLIFrameElement;
  state: State;
  onReady: (seek: SeekFn) => void;
  onTime: (t: number, d: number) => void;
};

function poll(p: YTPlayer, onTime: (t: number, d: number) => void): number {
  return window.setInterval((): void => {
    const t = p.getCurrentTime();
    const d = p.getDuration();
    if (Number.isFinite(t) && Number.isFinite(d) && d > 0) onTime(t, d);
  }, POLL_MS);
}

function buildOnReady(a: AttachArgs): () => void {
  return (): void => {
    if (!a.state.player) return;
    window.clearTimeout(a.state.fbId);
    a.onReady((sec) => a.state.player?.seekTo(sec, true));
    a.state.pollId = poll(a.state.player, a.onTime);
  };
}

function attach(a: AttachArgs): void {
  const yt = getYT();
  if (!yt) return;
  try {
    a.state.player = new yt.Player(a.iframe, {
      events: { onReady: buildOnReady(a) },
    });
  } catch {
    /* attach failed — fbId fallback fires onReady with no-op seek */
  }
}

function teardown(s: State): void {
  window.clearTimeout(s.fbId);
  if (s.pollId) window.clearInterval(s.pollId);
  try {
    s.player?.destroy();
  } catch {
    /* noop */
  }
}

function tryAttach(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  state: State,
  refs: Refs
): void {
  const iframe = iframeRef.current;
  if (!iframe) return;
  attach({
    iframe,
    state,
    onReady: (seek) => refs.onReadyRef.current(seek),
    onTime: (t, d) => refs.onTimeRef.current(t, d),
  });
}

export function setup(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  refs: Refs
): () => void {
  const s: State = {
    player: null,
    pollId: 0,
    fbId: window.setTimeout(
      () => refs.onReadyRef.current(() => undefined),
      API_FALLBACK_MS
    ),
  };
  let aborted = false;
  void loadYouTubeAPI().then(() => {
    if (!aborted) tryAttach(iframeRef, s, refs);
  });
  return (): void => {
    aborted = true;
    teardown(s);
  };
}
